import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Organizer {
  id: string;
  organizer_id: string;
  company_name?: string;
  description?: string;
  website?: string;
  sports: string[];
  rating: number;
  events_per_year: number;
  featured: boolean;
  avatar_url?: string;
  created_at: string;
  updated_at: string;
}

export function useOrganizers() {
  const [organizers, setOrganizers] = useState<Organizer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganizers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 1. Get public organizer profiles
      const { data: organizersData, error: organizersError } = await supabase
        .from('organizer_public_profiles')
        .select('*')
        .order('rating', { ascending: false });

      if (organizersError) throw organizersError;

      if (!organizersData || organizersData.length === 0) {
        setOrganizers([]);
        return;
      }

      // 2. For each organizer, count events using user_id directly from public profiles
      const organizersWithCounts = await Promise.all(
        organizersData.map(async (organizer) => {
          const realUserId = organizer.user_id;
          
          let totalEvents = 0;
          let upcomingEvents = 0;
          
          if (realUserId) {
            // Count ALL events (tournaments + leagues) using GET with range(0,0)
            const { count: totalCount, error: totalError } = await supabase
              .from('events')
              .select('id', { count: 'exact' })
              .eq('organizer_id', realUserId)
              .range(0, 0);
            
            if (totalError) {
              console.error(`Error counting events for organizer ${organizer.company_name}:`, totalError);
            }
            totalEvents = totalCount || 0;

            // Count upcoming events
            const { count: upcomingCount, error: upcomingError } = await supabase
              .from('events')
              .select('id', { count: 'exact' })
              .eq('organizer_id', realUserId)
              .eq('status', 'upcoming')
              .gt('start_date', new Date().toISOString())
              .range(0, 0);
            
            if (upcomingError) {
              console.error(`Error counting upcoming events for organizer ${organizer.company_name}:`, upcomingError);
            }
            upcomingEvents = upcomingCount || 0;
          }

          return {
            ...organizer,
            events_per_year: totalEvents,
            upcomingEvents: upcomingEvents
          };
        })
      );

      setOrganizers(organizersWithCounts as any);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizers();
  }, []);

  // Listen for review submissions to auto-refresh ratings
  useEffect(() => {
    const handler = () => {
      console.log('🔄 Organizer review submitted, refetching organizers...');
      fetchOrganizers();
    };
    window.addEventListener('organizer-review-submitted', handler);
    return () => window.removeEventListener('organizer-review-submitted', handler);
  }, []);

  const refetch = () => {
    fetchOrganizers();
  };

  return { organizers, loading, error, refetch };
}

export function useFeaturedOrganizers() {
  const { organizers, loading, error, refetch } = useOrganizers();

  const PRIORITY = ['racket admin', 'cpam', 'club axomiatla'];
  const norm = (s?: string) =>
    (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

  const featured = PRIORITY
    .map(name => organizers.find(o => norm((o as any).company_name) === name))
    .filter(Boolean) as typeof organizers;

  return { organizers: featured, loading, error, refetch };
}